import {
  Component,
  h,
  Prop,
  Event,
  EventEmitter,
  State,
  ComponentInterface,
} from "@stencil/core";
import { ICallbackInfo, IThemeOptions } from "../../models";
import {
  TranslateService,
  ThemeService,
  DateTimeFactory,
  SecurityService,
  AppConfigService as Config,
  DeviceService,
  StorageService,
  AppConfigService,
  ErrorHandler,
} from "../../services";
import { HandledError } from "../../models/HandledError";
import { ErrorCode } from "../../models/enums";
import { AVBusService } from "../../services/bus.service";
import { Schedule } from "@auvious/utils";
import { ScheduleService } from "../../services/schedule.service";
import { IScheduleRequest } from "../../models/schedule/ISchedule";

enum step {
  collection,
  confirm,
  complete,
}

interface IState {
  rendered?: boolean;
  cardVisible?: boolean;
  bodyVisible?: boolean;
  workHoursVisible?: boolean;
  step?: step;
  date?: Date;
  loadingSchedule?: boolean;
  loadingOTP?: boolean;
  disclaimerAccepted?: boolean;
  errors?: {
    name?: ValidityState;
    phone?: ValidityState;
    date?: boolean;
    schedule?: string;
  };
}

const DEFAULT_STATE: IState = {
  rendered: false,
  bodyVisible: false,
  cardVisible: false,
  workHoursVisible: false,
  step: step.collection,
  date: null,
  loadingSchedule: false,
  loadingOTP: false,
  disclaimerAccepted: false,
  errors: {},
};

const STORAGE_KEY_SCHEDULE = "auvious.scheduledVideoCall";

@Component({
  tag: "app-schedule",
  styleUrl: "app-schedule.scss",
  shadow: true,
})
export class AppSchedule implements ComponentInterface {
  @Prop() open: boolean = false;

  @Event({ bubbles: false }) close: EventEmitter;

  @State() state: IState = { ...DEFAULT_STATE };
  @State() themeOptions: IThemeOptions;

  private form: HTMLFormElement;
  private scheduleRequest: IScheduleRequest;
  private proposedDateTime: Date;
  private verificationCode: string;
  private ticket: string;
  private ticketUrl: string;
  private otp: string;
  private schedule: Schedule = null;

  constructor() {
    ThemeService.optionsAvailable().subscribe((o) => (this.themeOptions = o));
  }

  componentWillLoad() {
    this.proposedDateTime = ScheduleService.getProposedDateTime();
    this.schedule = new Schedule(Config.schedule);
    this.checkForExistingSchedule();
    this.setState({ date: this.proposedDateTime });
  }

  componentDidLoad(): void {
    this.setState({
      cardVisible: true,
      bodyVisible: true,
      disclaimerAccepted: !AppConfigService.scheduleDisclaimerURL,
    });
  }

  disconnectedCallback(): void {
    // this.contact = {};
    this.scheduleRequest = null;
    this.setState({
      ...DEFAULT_STATE,
      cardVisible: false,
      date: ScheduleService.getProposedDateTime(),
    });
    if (!this.form) {
      return;
    }
    // @ts-ignore
    for (let item of this.form.elements) {
      item.value = null;
    }
  }

  private setState(state: IState) {
    this.state = { ...this.state, ...state };
  }

  private checkForExistingSchedule() {
    ErrorHandler.addBreadcrumb("Schedule | existing check");
    const existingSchedule = this.getStoredScheduleData();
    if (existingSchedule) {
      // check if we missed it
      const isValidSchedule = DateTimeFactory.create(
        existingSchedule.scheduledAt
      ).isAfter(new Date());
      if (isValidSchedule) {
        this.ticketUrl = existingSchedule.ticketUrl;
        this.ticket = existingSchedule.ticket;

        this.setState({
          date: existingSchedule.scheduledAt,
          step: step.complete,
        });
      } else {
        this.clearStoredScheduleData();
      }
    }
  }

  async requestOTP() {
    try {
      ErrorHandler.addBreadcrumb("Schedule | request otp");
      this.setState({ loadingOTP: true });
      this.verificationCode = await SecurityService.requestOTP(
        this.scheduleRequest.customer.contact.primaryPhoneCountryCode +
          this.scheduleRequest.customer.contact.primaryPhone
      );
    } catch (ex) {
      const error = `${TranslateService.instant(
        "Could not request OTP."
      )}  ${TranslateService.instant(
        "Please check your phone number and country code"
      )}`;
      this.setState({ errors: { ...this.state.errors, schedule: error } });
      AVBusService.errorLog(
        new HandledError(ex, ErrorCode.SECURITY_OTP_REQUEST_FAILED)
      );
      throw ex;
    } finally {
      this.setState({ loadingOTP: false });
    }
  }

  async trySchedule() {
    if (!this.otp) {
      return;
    }
    ErrorHandler.addBreadcrumb("Schedule | create");
    if (!this.verificationCode) {
      this.setState({
        errors: {
          ...this.state.errors,
          schedule: TranslateService.instant(
            "Verification code not found. Please request a new OTP"
          ),
        },
      });
      return;
    }

    try {
      this.setState({
        loadingSchedule: true,
        errors: { ...this.state.errors, schedule: null },
      });

      const response = await ScheduleService.create(
        {
          ...this.scheduleRequest,
          title: TranslateService.instant("Video call"),
          metadata: AppConfigService.customerMetadata,
        },
        {
          otp: this.otp,
          verificationCode: this.verificationCode,
        }
      );

      this.setState({ loadingSchedule: false, step: step.complete });

      this.ticket = response.scheduleEntityId;
      this.ticketUrl = `${this.scheduleBaseURL}/t/${response.scheduleEntityId}`;

      this.storeScheduleData({
        ticket: this.ticket,
        ticketUrl: this.ticketUrl,
        scheduledAt: this.scheduleRequest.start,
      });
    } catch (ex) {
      let message;
      switch (ex.message) {
        case "401":
          message = TranslateService.instant("Invalid OTP code.");
          break;
        // case '400':
        //   message = TranslateService.instant('Wrong callback queue name');
        //   break;
        // case "404":
        //   message = TranslateService.instant('Application id not found');
        //   break;
        default:
          message = TranslateService.instant(
            "Could not schedule the call. Something unexpected happened."
          );
          break;
      }
      AVBusService.errorLog(
        new HandledError(ex, ErrorCode.SECURITY_CALLBACK_CREATE_FAILED)
      );
      this.setState({
        loadingSchedule: false,
        errors: { ...this.state.errors, schedule: message },
      });
    }
  }

  reschedule() {
    this.clearStoredScheduleData();
    this.setState({
      step: step.collection,
      date: ScheduleService.getProposedDateTime(),
    });
  }

  private getStoredScheduleData(): ICallbackInfo {
    if (Config.scheduleMode !== "genesys-callback") {
      return;
    }
    try {
      const data = StorageService.getItem(STORAGE_KEY_SCHEDULE);
      if (!data) {
        return null;
      }
      const info = JSON.parse(data);
      return {
        ...info,
        scheduledAt: DateTimeFactory.create(info.scheduledAt).toDate(),
      };
    } catch (ex) {
      return null;
    }
  }

  private storeScheduleData(callback: ICallbackInfo) {
    if (Config.scheduleMode === "genesys-callback") {
      StorageService.setItem(STORAGE_KEY_SCHEDULE, JSON.stringify(callback));
    }
  }

  private clearStoredScheduleData() {
    StorageService.removeItem(STORAGE_KEY_SCHEDULE);
  }

  onClose() {
    this.close.emit();
  }

  back() {
    this.setState({
      step: step.collection,
      errors: { ...this.state.errors, schedule: null },
    });
  }

  pinChanged(e: CustomEvent) {
    this.otp = e.detail;
  }

  async collectionReady(e: CustomEvent<IScheduleRequest>) {
    this.scheduleRequest = e.detail;
    this.setState({
      step: step.confirm,
      date: this.scheduleRequest.start,
      disclaimerAccepted: true,
    });
    await this.requestOTP();
  }

  isValid(): boolean {
    let valid = true;
    // @ts-ignore
    for (let item of this.form.elements) {
      this.state = {
        ...this.state,
        errors: {
          ...this.state.errors,
          [item.name]: item.checkValidity() ? null : item.validity,
        },
      };
      valid = valid && item.checkValidity();
    }

    // check date validity
    if (
      DateTimeFactory.create(this.state.date).isBefore(DateTimeFactory.create())
    ) {
      valid = false;
      this.setState({ errors: { ...this.state.errors, date: true } });
    }

    // check open hours
    const open = this.schedule.getMinutes(
      this.state.date.getFullYear(),
      this.state.date.getMonth(),
      this.state.date.getDate(),
      this.state.date.getHours()
    )[this.state.date.getMinutes()].open;

    if (!open) {
      this.setState({
        errors: {
          ...this.state.errors,
          schedule: TranslateService.instant(
            "We are closed at this time. Please check the working hours."
          ),
        },
      });
      valid = false;
    }

    return valid;
  }

  copyToClipboard() {
    DeviceService.copyTextToClipboard(this.ticketUrl);
  }

  private get scheduleBaseURL() {
    return Config.customDomainURL || Config.auviousBaseUrl;
  }

  getCalendarLink(calendar: "google" | "ical" | "outlook") {
    switch (Config.scheduleMode) {
      case "genesys-callback":
        return `${this.scheduleBaseURL}/security/ticket/${this.ticket}/event?calendar=${calendar}`;
      case "genesys-appointment":
      case "talkdesk-appointment":
        if (calendar === "ical" || calendar === "outlook") {
          return `${this.scheduleBaseURL}/cal/events/ical/${this.ticket}/event`;
        } else {
          return `${this.scheduleBaseURL}/cal/events/${calendar}/${this.ticket}/event`;
        }
    }
  }

  render() {
    const startOverText = TranslateService.instant("Start over");
    const downloadICSText = TranslateService.instant("Download .ics");
    const addToGoogleCalendarText = TranslateService.instant(
      "Add to Google Calendar"
    );
    const addToOutlookText = TranslateService.instant("Add to Outlook");
    const backText = TranslateService.instant("Back");
    const requestCodeText = TranslateService.instant("Request new code");
    const scheduleText = TranslateService.instant("Schedule");
    const scheduledAt =
      this.state.date &&
      DateTimeFactory.create(this.state.date).format("dddd MMMM Do, hh:mm A");
    const timeDifference =
      this.state.date && DateTimeFactory.create(this.state.date).format("Z");
    const callbackTicket = this.ticketUrl;
    const googleCalandarLink = this.getCalendarLink("google");
    const icsLink = this.getCalendarLink("ical");
    const outlookLink = this.getCalendarLink("outlook");

    return (
      <av-card
        data-tid="schedule/container"
        role="dialog"
        open={this.state.cardVisible}
        class={{
          "av-card-callback": true,
          "av-card-callback-timeslots": Config.schedulePicker === "timeslots",
        }}
      >
        <div slot="card-body">
          <h3 class="title av-direction">
            <av-translate>Schedule a video call</av-translate>
          </h3>
          <app-work-hours></app-work-hours>
          {this.state.step === step.collection && [
            Config.schedulePicker === "standard" && (
              <app-schedule-standard
                scheduledAt={this.scheduleRequest?.start}
                contact={this.scheduleRequest?.customer}
                isDisclaimerAccepted={this.state.disclaimerAccepted}
                onReady={this.collectionReady.bind(this)}
              ></app-schedule-standard>
            ),
            Config.schedulePicker === "timeslots" && (
              <app-schedule-timeslots
                scheduledAt={this.scheduleRequest?.start}
                contact={this.scheduleRequest?.customer}
                isDisclaimerAccepted={this.state.disclaimerAccepted}
                onReady={this.collectionReady.bind(this)}
              ></app-schedule-timeslots>
            ),
          ]}
          {this.state.step === step.confirm && (
            <div>
              <div class="av-direction">
                {scheduledAt} <small class="text-muted">{timeDifference}</small>
              </div>
              <p>
                <div class="av-direction">
                  <small>
                    <av-translate>Please enter the 6-digit code</av-translate>
                  </small>
                </div>
                <av-pin
                  type="number"
                  onChanged={this.pinChanged.bind(this)}
                ></av-pin>
                <div class="av-direction">
                  <av-button
                    data-tid="appointment/otp"
                    color="link"
                    onClick={this.requestOTP.bind(this)}
                  >
                    {requestCodeText}
                  </av-button>
                </div>
              </p>

              {this.state.errors && this.state.errors.schedule && (
                <div class="form-field form-field-error-panel">
                  {this.state.errors.schedule}
                </div>
              )}

              <div class="av-confirm-actions">
                <av-button
                  data-tid="appointment/back-to-otp"
                  color="basic"
                  onClick={this.back.bind(this)}
                >
                  &larr; {backText}
                </av-button>
                <av-button
                  data-tid="appointment/schedule"
                  color="accent"
                  loading={this.state.loadingSchedule}
                  onClick={this.trySchedule.bind(this)}
                >
                  {scheduleText}
                </av-button>
              </div>
            </div>
          )}
          {this.state.step === step.complete && (
            <div>
              <div class="av-direction">
                <av-translate>Video call scheduled for</av-translate>
              </div>
              <div class="av-direction">
                <b>
                  {scheduledAt}{" "}
                  <small class="text-muted">{timeDifference}</small>
                </b>
              </div>
              {Config.scheduleMode === "genesys-callback" && (
                <div class="av-panel-link">
                  <div class="av-textarea-container">
                    <span>
                      <av-translate>Room Link</av-translate>
                    </span>
                    <textarea disabled>{callbackTicket}</textarea>
                  </div>
                  <div>
                    <av-button
                      data-tid="apppointment/copy-url"
                      ariaLabel={TranslateService.instant(
                        "Copy room link to clipboard"
                      )}
                      style={{ width: "40px", display: "inline-block" }}
                      color="basic"
                      size="md"
                      shape="round"
                      tooltip="Copy"
                      onClick={this.copyToClipboard.bind(this)}
                    >
                      <av-asset color="theme" image="copy-document"></av-asset>
                    </av-button>
                  </div>
                </div>
              )}
              <p class="av-direction">
                <av-translate>
                  Please add this call to your calendar.
                </av-translate>
              </p>
              <div class="av-calendar-actions">
                <av-button
                  data-tid="appointment/google-calendar"
                  link={googleCalandarLink}
                  size="block"
                  color="accent"
                >
                  {addToGoogleCalendarText}
                </av-button>
                <av-button
                  data-tid="appointment/outlook"
                  link={outlookLink}
                  size="block"
                  color="accent"
                >
                  {addToOutlookText}
                </av-button>
                <av-button
                  data-tid="appointment/ics"
                  link={icsLink}
                  size="block"
                  color="accent"
                >
                  {downloadICSText}
                </av-button>
                <av-button
                  data-tid="appointment/reschedule"
                  size="block"
                  color="link"
                  onClick={this.reschedule.bind(this)}
                >
                  {startOverText}
                </av-button>
              </div>
            </div>
          )}
        </div>
      </av-card>
    );
  }
}
